import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Save,
  Mail,
  MessageCircle,
  ArrowLeft,
  Code,
  Eye,
  Calendar,
  Globe,
  User,
  Building,
  Info,
  RotateCcw,
  HardDrive,
  Database,
  Download,
  Cloud,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Server,
  Send,
  Loader2,
  Zap
} from "lucide-react";

// Default templates for 3 email stages
const DEFAULT_EMAIL_TEMPLATES = {
  first: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #2563eb; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; }
    .footer { background: #1f2937; color: #9ca3af; padding: 15px; text-align: center; font-size: 12px; border-radius: 0 0 8px 8px; }
    .cta-button { display: inline-block; background: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
    .highlight { color: #2563eb; font-weight: bold; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Opportunità di Collaborazione</h1>
    </div>
    <div class="content">
      <p>Gentile <span class="highlight">{{NOME}} {{COGNOME}}</span>,</p>
      
      <p>Mi chiamo Max Valle e faccio parte del capitolo BNI <strong>{{CAPITOLO}}</strong>.</p>
      
      <p>Ho avuto modo di visitare il vostro sito web e sono rimasto colpito dalla qualità dei vostri servizi. 
      Credo che potremmo trovare interessanti opportunità di collaborazione reciproca.</p>
      
      <p>Mi piacerebbe organizzare una breve call conoscitiva per esplorare eventuali sinergie.</p>
      
      <p><a href="{{LINK_CALENDAR}}" class="cta-button">Prenota un appuntamento</a></p>
      
      <p>Cordiali saluti,<br>
      <strong>Max Valle</strong></p>
    </div>
    <div class="footer">
      <p>Questo messaggio è stato inviato tramite BNI Tracker</p>
    </div>
  </div>
</body>
</html>`,
  second: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #059669; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; }
    .footer { background: #1f2937; color: #9ca3af; padding: 15px; text-align: center; font-size: 12px; border-radius: 0 0 8px 8px; }
    .cta-button { display: inline-block; background: #059669; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
    .highlight { color: #059669; font-weight: bold; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Follow-up: Opportunità di Collaborazione</h1>
    </div>
    <div class="content">
      <p>Gentile <span class="highlight">{{NOME}} {{COGNOME}}</span>,</p>
      
      <p>Spero che questo messaggio ti trovi bene.</p>
      
      <p>Ti scrivo per dare seguito alla mia precedente email riguardo una possibile collaborazione. 
      Capisco che sei molto impegnato/a, ma credo davvero che potremmo trovare sinergie interessanti.</p>
      
      <p>Saresti disponibile per una breve chiamata di 15 minuti questa settimana?</p>
      
      <p><a href="{{LINK_CALENDAR}}" class="cta-button">Scegli un orario</a></p>
      
      <p>Grazie per il tuo tempo,<br>
      <strong>Max Valle</strong><br>
      BNI {{CAPITOLO}}</p>
    </div>
    <div class="footer">
      <p>Questo messaggio è stato inviato tramite BNI Tracker</p>
    </div>
  </div>
</body>
</html>`,
  third: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #dc2626; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; }
    .footer { background: #1f2937; color: #9ca3af; padding: 15px; text-align: center; font-size: 12px; border-radius: 0 0 8px 8px; }
    .cta-button { display: inline-block; background: #dc2626; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
    .highlight { color: #dc2626; font-weight: bold; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Ultimo tentativo di contatto</h1>
    </div>
    <div class="content">
      <p>Gentile <span class="highlight">{{NOME}} {{COGNOME}}</span>,</p>
      
      <p>Ti ho scritto un paio di volte nelle ultime settimane senza ricevere risposta.</p>
      
      <p>Capisco perfettamente che potresti non essere interessato/a o che questo non sia il momento giusto. 
      Non voglio essere insistente, quindi questo sarà il mio ultimo messaggio.</p>
      
      <p>Se in futuro dovessi essere interessato/a a esplorare possibili collaborazioni, 
      sarò felice di sentirti.</p>
      
      <p><a href="{{LINK_CALENDAR}}" class="cta-button">Prenota quando vuoi</a></p>
      
      <p>Ti auguro il meglio,<br>
      <strong>Max Valle</strong><br>
      BNI {{CAPITOLO}}</p>
    </div>
    <div class="footer">
      <p>Questo messaggio è stato inviato tramite BNI Tracker</p>
    </div>
  </div>
</body>
</html>`
};

// Default templates for 3 WhatsApp stages
const DEFAULT_WHATSAPP_TEMPLATES = {
  first: `Ciao {{NOME}}! 👋

Sono Max Valle del capitolo BNI {{CAPITOLO}}.

Ho visitato il tuo sito web e mi piacerebbe parlare di possibili collaborazioni.

Ti va di fare una breve call? Puoi prenotare direttamente qui: {{LINK_CALENDAR}}

A presto!`,
  second: `Ciao {{NOME}}! 👋

Ti ho scritto qualche giorno fa riguardo una possibile collaborazione.

Capisco che sei impegnato/a, ma credo davvero che potremmo trovare delle sinergie interessanti.

Fammi sapere se hai 15 minuti liberi questa settimana: {{LINK_CALENDAR}}

Grazie! 🙏`,
  third: `Ciao {{NOME}},

Ti scrivo un'ultima volta per non essere troppo insistente.

Se in futuro sei interessato/a a collaborare, il link per prenotare una call è sempre attivo: {{LINK_CALENDAR}}

Ti auguro il meglio! 🍀
Max Valle - BNI {{CAPITOLO}}`
};

const DEFAULT_EMAIL_SUBJECTS = {
  first: "Opportunità di collaborazione - BNI {{CAPITOLO}}",
  second: "Follow-up: Opportunità di collaborazione",
  third: "Ultimo messaggio - Collaborazione BNI"
};

const PLACEHOLDERS = [
  { code: "{{NOME}}", description: "Nome del membro", icon: User },
  { code: "{{COGNOME}}", description: "Cognome del membro", icon: User },
  { code: "{{EMAIL}}", description: "Email del contatto", icon: Mail },
  { code: "{{CAPITOLO}}", description: "Nome del capitolo BNI", icon: Building },
  { code: "{{LINK_CALENDAR_BNI}}", description: "Link calendario BNI", icon: Calendar },
  { code: "{{LINK_CALENDAR_STANDARD}}", description: "Link calendario standard", icon: Calendar },
  { code: "{{LINK_ANALISI_SITO}}", description: "Link dinamico analisi sito", icon: Globe },
  { code: "{{PAGESPEED_MOBILE}}", description: "Punteggio PageSpeed Mobile", icon: Globe },
  { code: "{{SEOZOOM_AUTHORITY}}", description: "SEOZoom Authority (ZA)", icon: Globe },
  { code: "{{SEOZOOM_TRAFFIC}}", description: "SEOZoom Traffico Stimato", icon: Globe },
];

const SettingsPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("prospect");
  const [activeEmailStage, setActiveEmailStage] = useState("first");
  const [activeWhatsappStage, setActiveWhatsappStage] = useState("first");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Settings state - 3 templates each
  const [emailTemplates, setEmailTemplates] = useState(DEFAULT_EMAIL_TEMPLATES);
  const [whatsappTemplates, setWhatsappTemplates] = useState(DEFAULT_WHATSAPP_TEMPLATES);
  const [emailSubjects, setEmailSubjects] = useState(DEFAULT_EMAIL_SUBJECTS);
  const [calendarLinkBni, setCalendarLinkBni] = useState("");
  const [calendarLinkStandard, setCalendarLinkStandard] = useState("");
  
  // Prospect Email state
  const [prospectEmailTemplate, setProspectEmailTemplate] = useState({
    subject: "Opportunità per la tua attività",
    body: `Gentile {nome},

Mi chiamo Max Valle e sono un consulente di marketing digitale.

Ho avuto modo di analizzare il vostro sito web e ho notato alcune opportunità di miglioramento che potrebbero incrementare significativamente la vostra visibilità online.

Saresti interessato ad una breve consulenza gratuita per scoprire come ottimizzare la tua presenza online?

Cordiali saluti,
Max Valle
MaxValle.it`
  });
  
  // Backup state
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isBackingUpDb, setIsBackingUpDb] = useState(false);
  const [backupList, setBackupList] = useState([]);
  const [isLoadingBackups, setIsLoadingBackups] = useState(false);
  const [lastBackupResult, setLastBackupResult] = useState(null);
  
  // SMTP state
  const [smtpSettings, setSmtpSettings] = useState({
    host: "",
    port: "587",
    username: "",
    password: "",
    from_email: "",
    from_name: "Max Valle",
    use_tls: true
  });
  const [isSavingSmtp, setIsSavingSmtp] = useState(false);
  const [isTestingSmtp, setIsTestingSmtp] = useState(false);
  
  // Email provider state (smtp or resend)
  const [emailProvider, setEmailProvider] = useState("smtp");
  const [resendSettings, setResendSettings] = useState({
    from_email: "clienti@maxvalle.app",
    from_name: "Max Valle"
  });
  const [isTestingResend, setIsTestingResend] = useState(false);
  
  // Test email state
  const [testEmailRecipient, setTestEmailRecipient] = useState("");
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data) {
          // Load email templates
          if (response.data.email_templates) {
            setEmailTemplates({
              first: response.data.email_templates.first || DEFAULT_EMAIL_TEMPLATES.first,
              second: response.data.email_templates.second || DEFAULT_EMAIL_TEMPLATES.second,
              third: response.data.email_templates.third || DEFAULT_EMAIL_TEMPLATES.third
            });
          } else if (response.data.email_template) {
            // Legacy support - single template becomes "first"
            setEmailTemplates({
              ...DEFAULT_EMAIL_TEMPLATES,
              first: response.data.email_template
            });
          }
          
          // Load WhatsApp templates
          if (response.data.whatsapp_templates) {
            setWhatsappTemplates({
              first: response.data.whatsapp_templates.first || DEFAULT_WHATSAPP_TEMPLATES.first,
              second: response.data.whatsapp_templates.second || DEFAULT_WHATSAPP_TEMPLATES.second,
              third: response.data.whatsapp_templates.third || DEFAULT_WHATSAPP_TEMPLATES.third
            });
          } else if (response.data.whatsapp_template) {
            // Legacy support
            setWhatsappTemplates({
              ...DEFAULT_WHATSAPP_TEMPLATES,
              first: response.data.whatsapp_template
            });
          }
          
          // Load email subjects
          if (response.data.email_subjects) {
            setEmailSubjects({
              first: response.data.email_subjects.first || DEFAULT_EMAIL_SUBJECTS.first,
              second: response.data.email_subjects.second || DEFAULT_EMAIL_SUBJECTS.second,
              third: response.data.email_subjects.third || DEFAULT_EMAIL_SUBJECTS.third
            });
          } else if (response.data.email_subject) {
            setEmailSubjects({
              ...DEFAULT_EMAIL_SUBJECTS,
              first: response.data.email_subject
            });
          }
          
          // Load prospect email template
          if (response.data.prospect_email_template) {
            setProspectEmailTemplate({
              subject: response.data.prospect_email_template.subject || prospectEmailTemplate.subject,
              body: response.data.prospect_email_template.body || prospectEmailTemplate.body
            });
          }
          
          // Load SMTP settings
          if (response.data.smtp_settings) {
            setSmtpSettings({
              host: response.data.smtp_settings.host || "",
              port: response.data.smtp_settings.port || "587",
              username: response.data.smtp_settings.username || "",
              password: response.data.smtp_settings.password || "",
              from_email: response.data.smtp_settings.from_email || "",
              from_name: response.data.smtp_settings.from_name || "Max Valle",
              use_tls: response.data.smtp_settings.use_tls !== false
            });
          }
          
          // Load email provider
          if (response.data.email_provider) {
            setEmailProvider(response.data.email_provider);
          }
          
          // Load Resend settings
          if (response.data.resend_from_email) {
            setResendSettings({
              from_email: response.data.resend_from_email || "clienti@maxvalle.app",
              from_name: response.data.resend_from_name || "Max Valle"
            });
          }
          
          // Load calendar links (new dual system)
          if (response.data.calendar_link_bni !== undefined) {
            setCalendarLinkBni(response.data.calendar_link_bni || "");
          } else if (response.data.calendar_link) {
            // Legacy support - old single link becomes BNI link
            setCalendarLinkBni(response.data.calendar_link || "");
          }
          setCalendarLinkStandard(response.data.calendar_link_standard || "");
        }
      } catch (error) {
        console.log("No existing settings, using defaults");
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSettings();
  }, [token]);

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      await axios.post(`${API}/settings`, {
        email_templates: emailTemplates,
        whatsapp_templates: whatsappTemplates,
        email_subjects: emailSubjects,
        calendar_link_bni: calendarLinkBni,
        calendar_link_standard: calendarLinkStandard,
        prospect_email_template: prospectEmailTemplate,
        smtp_settings: smtpSettings,
        email_provider: emailProvider,
        resend_from_email: resendSettings.from_email,
        resend_from_name: resendSettings.from_name,
        // Legacy fields for backward compatibility
        email_template: emailTemplates.first,
        whatsapp_template: whatsappTemplates.first,
        email_subject: emailSubjects.first,
        calendar_link: calendarLinkBni  // Legacy: use BNI link as default
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Impostazioni salvate con successo!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Errore nel salvataggio delle impostazioni");
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetEmail = (stage) => {
    setEmailTemplates(prev => ({
      ...prev,
      [stage]: DEFAULT_EMAIL_TEMPLATES[stage]
    }));
    setEmailSubjects(prev => ({
      ...prev,
      [stage]: DEFAULT_EMAIL_SUBJECTS[stage]
    }));
    toast.info(`Template email "${stage === 'first' ? 'Prima' : stage === 'second' ? 'Seconda' : 'Terza'}" ripristinato`);
  };

  const handleResetWhatsapp = (stage) => {
    setWhatsappTemplates(prev => ({
      ...prev,
      [stage]: DEFAULT_WHATSAPP_TEMPLATES[stage]
    }));
    toast.info(`Template WhatsApp "${stage === 'first' ? 'Primo' : stage === 'second' ? 'Secondo' : 'Terzo'}" ripristinato`);
  };

  const getPreviewContent = (template) => {
    return template
      .replace(/\{\{NOME\}\}/g, "Mario")
      .replace(/\{\{COGNOME\}\}/g, "Rossi")
      .replace(/\{\{CAPITOLO\}\}/g, "Stupor Mundi Rozzano")
      .replace(/\{\{LINK_CALENDAR_BNI\}\}/g, calendarLinkBni || "https://calendar.google.com/bni...")
      .replace(/\{\{LINK_CALENDAR_STANDARD\}\}/g, calendarLinkStandard || "https://calendar.google.com/standard...")
      .replace(/\{\{LINK_CALENDAR\}\}/g, calendarLinkBni || "https://calendar.google.com/...")  // Legacy
      .replace(/\{\{LINK_ANALISI_SITO\}\}/g, "https://example.com/analisi/123")
      .replace(/\{\{PAGESPEED_MOBILE\}\}/g, "85")
      .replace(/\{\{SEOZOOM_AUTHORITY\}\}/g, "32")
      .replace(/\{\{SEOZOOM_TRAFFIC\}\}/g, "1.250");
  };

  const updateEmailTemplate = (stage, value) => {
    setEmailTemplates(prev => ({ ...prev, [stage]: value }));
  };

  const updateWhatsappTemplate = (stage, value) => {
    setWhatsappTemplates(prev => ({ ...prev, [stage]: value }));
  };

  const updateEmailSubject = (stage, value) => {
    setEmailSubjects(prev => ({ ...prev, [stage]: value }));
  };

  // Backup functions
  const handleFullBackup = async () => {
    setIsBackingUp(true);
    setLastBackupResult(null);
    
    try {
      const response = await axios.post(`${API}/backup/full`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000
      });
      
      setLastBackupResult({
        success: true,
        type: 'full',
        filename: response.data.filename,
        collections: response.data.collections_exported
      });
      toast.success("Backup completo eseguito con successo!");
      loadBackupList();
    } catch (error) {
      console.error("Full backup error:", error);
      setLastBackupResult({
        success: false,
        type: 'full',
        error: error.response?.data?.detail || error.message
      });
      toast.error("Errore nel backup completo");
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleDatabaseBackup = async () => {
    setIsBackingUpDb(true);
    setLastBackupResult(null);
    
    try {
      const response = await axios.post(`${API}/backup/database`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000
      });
      
      setLastBackupResult({
        success: true,
        type: 'database',
        filename: response.data.filename,
        collections: response.data.collections_exported
      });
      toast.success("Backup database eseguito con successo!");
      loadBackupList();
    } catch (error) {
      console.error("Database backup error:", error);
      setLastBackupResult({
        success: false,
        type: 'database',
        error: error.response?.data?.detail || error.message
      });
      toast.error("Errore nel backup database");
    } finally {
      setIsBackingUpDb(false);
    }
  };

  const loadBackupList = async () => {
    setIsLoadingBackups(true);
    try {
      const response = await axios.get(`${API}/backup/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackupList(response.data.backups || []);
    } catch (error) {
      console.error("Error loading backups:", error);
    } finally {
      setIsLoadingBackups(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'backup') {
      loadBackupList();
    }
  }, [activeTab]);

  const stageLabels = {
    first: { label: "Prima", color: "bg-blue-100 text-blue-800" },
    second: { label: "Seconda", color: "bg-green-100 text-green-800" },
    third: { label: "Terza", color: "bg-red-100 text-red-800" }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b glass-header">
        <div className="container-app flex h-16 items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/chapters">
              <Button variant="ghost" size="icon" data-testid="back-button">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Impostazioni</h1>
              <p className="text-sm text-muted-foreground">Template email, WhatsApp e backup</p>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-animate"
            data-testid="save-settings-button"
          >
            {isSaving ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salva Impostazioni
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container-app py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Calendar Links Card */}
          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Link Calendari
              </CardTitle>
              <CardDescription>
                Configura i link ai tuoi calendari per le prenotazioni
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">BNI</Badge>
                    Calendario BNI
                  </Label>
                  <Input
                    placeholder="https://calendar.google.com/calendar/appointments/..."
                    value={calendarLinkBni}
                    onChange={(e) => setCalendarLinkBni(e.target.value)}
                    data-testid="calendar-link-bni-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Usato con <code className="bg-muted px-1 rounded">{"{{LINK_CALENDAR_BNI}}"}</code>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-green-100 text-green-800">Standard</Badge>
                    Calendario Standard
                  </Label>
                  <Input
                    placeholder="https://calendar.google.com/calendar/appointments/..."
                    value={calendarLinkStandard}
                    onChange={(e) => setCalendarLinkStandard(e.target.value)}
                    data-testid="calendar-link-standard-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Usato con <code className="bg-muted px-1 rounded">{"{{LINK_CALENDAR_STANDARD}}"}</code>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Placeholders Info */}
          <Card className="border-border/60 bg-muted/30">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Info className="h-4 w-4 text-primary" />
                Placeholder Disponibili
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {PLACEHOLDERS.map((p) => (
                  <Badge key={p.code} variant="secondary" className="gap-1 py-1">
                    <p.icon className="h-3 w-3" />
                    <code className="text-xs">{p.code}</code>
                    <span className="text-xs text-muted-foreground">- {p.description}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Templates Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full max-w-lg grid-cols-3">
              <TabsTrigger value="prospect" className="gap-2" data-testid="prospect-tab">
                <Mail className="h-4 w-4" />
                Email Prospect
              </TabsTrigger>
              <TabsTrigger value="smtp" className="gap-2" data-testid="smtp-tab">
                <Server className="h-4 w-4" />
                SMTP
              </TabsTrigger>
              <TabsTrigger value="backup" className="gap-2" data-testid="backup-tab">
                <HardDrive className="h-4 w-4" />
                Backup
              </TabsTrigger>
            </TabsList>

            {/* Prospect Email Tab - Now with HTML Editor */}
            <TabsContent value="prospect" className="space-y-4">
              <Card className="border-border/60">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5 text-orange-500" />
                        Template Email Prospect
                      </CardTitle>
                      <CardDescription>
                        Email per contattare nuovi prospect (lead da Google My Business)
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Subject */}
                  <div className="space-y-2">
                    <Label>Oggetto Email</Label>
                    <Input
                      value={prospectEmailTemplate.subject}
                      onChange={(e) => setProspectEmailTemplate(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Oggetto dell'email..."
                      data-testid="prospect-email-subject"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* HTML Editor */}
                    <div className="space-y-2">
                      <Label>Codice HTML</Label>
                      <Textarea
                        value={prospectEmailTemplate.body}
                        onChange={(e) => setProspectEmailTemplate(prev => ({ ...prev, body: e.target.value }))}
                        className="min-h-[400px] font-mono text-sm"
                        placeholder="Inserisci il template HTML..."
                        data-testid="prospect-email-body"
                      />
                    </div>

                    {/* Preview */}
                    <div className="space-y-2">
                      <Label>Anteprima</Label>
                      <div 
                        className="border rounded-lg p-4 min-h-[400px] bg-white overflow-auto"
                        dangerouslySetInnerHTML={{ 
                          __html: prospectEmailTemplate.body
                            .replace(/\{nome\}/gi, "Mario Rossi")
                            .replace(/\{attivita\}/gi, "Studio Esempio")
                            .replace(/\{\{PAGESPEED_MOBILE\}\}/gi, "85")
                            .replace(/\{\{SEOZOOM_AUTHORITY\}\}/gi, "32")
                            .replace(/\{\{SEOZOOM_TRAFFIC\}\}/gi, "1.250")
                            .replace(/\{\{LINK_CALENDAR_BNI\}\}/gi, calendarLinkBni || "#")
                            .replace(/\{\{LINK_CALENDAR_STANDARD\}\}/gi, calendarLinkStandard || "#")
                        }}
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Placeholder disponibili: <code className="bg-muted px-1 rounded">{"{nome}"}</code>, <code className="bg-muted px-1 rounded">{"{attivita}"}</code>, 
                    e tutti i placeholder SEO/calendario mostrati sopra.
                  </p>
                  
                  {/* Test Email Section */}
                  <div className="border-t pt-4 mt-4">
                    <Label className="mb-2 block">Invia Email di Test</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="email@esempio.it"
                        value={testEmailRecipient}
                        onChange={(e) => setTestEmailRecipient(e.target.value)}
                        className="max-w-xs"
                        data-testid="prospect-test-email-input"
                      />
                      <Button
                        variant="outline"
                        onClick={async () => {
                          if (!testEmailRecipient) {
                            toast.error("Inserisci un indirizzo email");
                            return;
                          }
                          setIsSendingTestEmail(true);
                          try {
                            await axios.post(`${API}/email/test`, {
                              to_email: testEmailRecipient,
                              subject: prospectEmailTemplate.subject,
                              body: prospectEmailTemplate.body
                                .replace(/\{nome\}/gi, "Mario Rossi")
                                .replace(/\{attivita\}/gi, "Studio Esempio")
                            }, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success("Email di test inviata!");
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Errore nell'invio dell'email");
                          } finally {
                            setIsSendingTestEmail(false);
                          }
                        }}
                        disabled={isSendingTestEmail}
                        data-testid="prospect-send-test-button"
                      >
                        {isSendingTestEmail ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4 mr-2" />
                        )}
                        Invia Test
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* EMAIL PROVIDER Tab */}
            <TabsContent value="smtp" className="space-y-4">
              {/* Provider Selection */}
              <Card className="border-border/60">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-600" />
                    Provider Email
                  </CardTitle>
                  <CardDescription>
                    Seleziona il servizio per l&apos;invio delle email
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${emailProvider === 'resend' ? 'border-orange-500 bg-orange-50' : 'border-border hover:border-gray-400'}`}>
                      <input
                        type="radio"
                        name="emailProvider"
                        value="resend"
                        checked={emailProvider === 'resend'}
                        onChange={() => setEmailProvider('resend')}
                        className="w-4 h-4 text-orange-500"
                      />
                      <div>
                        <p className="font-medium">Resend</p>
                        <p className="text-sm text-muted-foreground">Consigliato - Semplice e affidabile</p>
                      </div>
                    </label>
                    <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${emailProvider === 'smtp' ? 'border-green-500 bg-green-50' : 'border-border hover:border-gray-400'}`}>
                      <input
                        type="radio"
                        name="emailProvider"
                        value="smtp"
                        checked={emailProvider === 'smtp'}
                        onChange={() => setEmailProvider('smtp')}
                        className="w-4 h-4 text-green-500"
                      />
                      <div>
                        <p className="font-medium">SMTP</p>
                        <p className="text-sm text-muted-foreground">Server SMTP personalizzato</p>
                      </div>
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Resend Settings */}
              {emailProvider === 'resend' && (
                <Card className="border-border/60 border-orange-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-orange-500" />
                      Configurazione Resend
                    </CardTitle>
                    <CardDescription>
                      Le email verranno inviate tramite Resend.com
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email Mittente</Label>
                        <Input
                          value={resendSettings.from_email}
                          onChange={(e) => setResendSettings(prev => ({ ...prev, from_email: e.target.value }))}
                          placeholder="clienti@tuodominio.com"
                        />
                        <p className="text-xs text-muted-foreground">Il dominio deve essere verificato su Resend</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Nome Mittente</Label>
                        <Input
                          value={resendSettings.from_name}
                          onChange={(e) => setResendSettings(prev => ({ ...prev, from_name: e.target.value }))}
                          placeholder="Max Valle"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 pt-4 border-t">
                      <Input
                        placeholder="email@test.com"
                        value={testEmailRecipient}
                        onChange={(e) => setTestEmailRecipient(e.target.value)}
                        className="max-w-xs"
                      />
                      <Button
                        variant="outline"
                        onClick={async () => {
                          if (!testEmailRecipient) {
                            toast.error("Inserisci un indirizzo email");
                            return;
                          }
                          setIsTestingResend(true);
                          try {
                            const response = await axios.post(`${API}/resend/test`, {
                              to_email: testEmailRecipient,
                              from_email: resendSettings.from_email,
                              from_name: resendSettings.from_name
                            }, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success(response.data.message);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Errore nel test");
                          } finally {
                            setIsTestingResend(false);
                          }
                        }}
                        disabled={isTestingResend}
                      >
                        {isTestingResend ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4 mr-2" />
                        )}
                        Invia Test Resend
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* SMTP Settings */}
              {emailProvider === 'smtp' && (
              <Card className="border-border/60">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-5 w-5 text-green-600" />
                    Configurazione Server SMTP
                  </CardTitle>
                  <CardDescription>
                    Configura i parametri per l&apos;invio delle email
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* SMTP Host */}
                    <div className="space-y-2">
                      <Label>Server SMTP</Label>
                      <Input
                        value={smtpSettings.host}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, host: e.target.value }))}
                        placeholder="smtp.gmail.com"
                        data-testid="smtp-host"
                      />
                    </div>
                    
                    {/* SMTP Port */}
                    <div className="space-y-2">
                      <Label>Porta</Label>
                      <Input
                        value={smtpSettings.port}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, port: e.target.value }))}
                        placeholder="587"
                        data-testid="smtp-port"
                      />
                    </div>
                    
                    {/* Username */}
                    <div className="space-y-2">
                      <Label>Username / Email</Label>
                      <Input
                        value={smtpSettings.username}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, username: e.target.value }))}
                        placeholder="tuaemail@gmail.com"
                        data-testid="smtp-username"
                      />
                    </div>
                    
                    {/* Password */}
                    <div className="space-y-2">
                      <Label>Password / App Password</Label>
                      <Input
                        type="password"
                        value={smtpSettings.password}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="••••••••••••"
                        data-testid="smtp-password"
                      />
                    </div>
                    
                    {/* From Email */}
                    <div className="space-y-2">
                      <Label>Email Mittente</Label>
                      <Input
                        value={smtpSettings.from_email}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, from_email: e.target.value }))}
                        placeholder="info@tuodominio.it"
                        data-testid="smtp-from-email"
                      />
                    </div>
                    
                    {/* From Name */}
                    <div className="space-y-2">
                      <Label>Nome Mittente</Label>
                      <Input
                        value={smtpSettings.from_name}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, from_name: e.target.value }))}
                        placeholder="Max Valle"
                        data-testid="smtp-from-name"
                      />
                    </div>
                  </div>
                  
                  {/* TLS Checkbox */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="use_tls"
                      checked={smtpSettings.use_tls}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, use_tls: e.target.checked }))}
                      className="rounded"
                    />
                    <Label htmlFor="use_tls" className="cursor-pointer">Usa TLS/STARTTLS (consigliato)</Label>
                  </div>
                  
                  {/* Test Result */}
                  {smtpTestResult && (
                    <div className={`p-3 rounded-lg flex items-center gap-2 ${smtpTestResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {smtpTestResult.success ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <AlertCircle className="h-5 w-5" />
                      )}
                      <span className="text-sm">{smtpTestResult.message}</span>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={async () => {
                        setIsTestingSmtp(true);
                        setSmtpTestResult(null);
                        try {
                          const response = await axios.post(`${API}/smtp/test`, smtpSettings, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setSmtpTestResult({ success: true, message: response.data.message || "Email di test inviata con successo!" });
                        } catch (error) {
                          setSmtpTestResult({ success: false, message: error.response?.data?.detail || "Errore nel test SMTP" });
                        } finally {
                          setIsTestingSmtp(false);
                        }
                      }}
                      disabled={isTestingSmtp || !smtpSettings.host || !smtpSettings.username}
                      data-testid="smtp-test-button"
                    >
                      {isTestingSmtp ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Invia Email di Test
                    </Button>
                  </div>
                  
                  {/* Info Box */}
                  <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
                    <p className="font-medium mb-2">Configurazioni comuni:</p>
                    <ul className="space-y-1 text-xs">
                      <li><strong>Gmail:</strong> smtp.gmail.com, porta 587, usa App Password</li>
                      <li><strong>Outlook:</strong> smtp-mail.outlook.com, porta 587</li>
                      <li><strong>Aruba:</strong> smtps.aruba.it, porta 465</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
              )}
            </TabsContent>

            {/* Backup Tab */}
            <TabsContent value="backup" className="space-y-4">
              {/* Backup Actions */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Full Backup Card */}
                <Card className="border-border/60">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <HardDrive className="h-5 w-5 text-blue-600" />
                      Backup Completo
                    </CardTitle>
                    <CardDescription>
                      Esporta l&apos;intero gestionale comprensivo di database e caricalo su FTP
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      onClick={handleFullBackup}
                      disabled={isBackingUp}
                      className="w-full gap-2"
                      data-testid="full-backup-button"
                    >
                      {isBackingUp ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Backup in corso...
                        </>
                      ) : (
                        <>
                          <Cloud className="h-4 w-4" />
                          Esegui Backup Completo
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                {/* Database Backup Card */}
                <Card className="border-border/60">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="h-5 w-5 text-green-600" />
                      Backup Database
                    </CardTitle>
                    <CardDescription>
                      Esporta solo i dati del database (membri, capitoli, impostazioni)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      onClick={handleDatabaseBackup}
                      disabled={isBackingUpDb}
                      variant="outline"
                      className="w-full gap-2"
                      data-testid="database-backup-button"
                    >
                      {isBackingUpDb ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Backup in corso...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4" />
                          Esegui Backup Database
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Last Backup Result */}
              {lastBackupResult && (
                <Card className={`border-border/60 ${lastBackupResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      {lastBackupResult.success ? (
                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                      )}
                      <div>
                        <p className={`font-medium ${lastBackupResult.success ? 'text-green-800' : 'text-red-800'}`}>
                          {lastBackupResult.success 
                            ? `Backup ${lastBackupResult.type === 'full' ? 'completo' : 'database'} completato!`
                            : `Errore nel backup ${lastBackupResult.type === 'full' ? 'completo' : 'database'}`
                          }
                        </p>
                        {lastBackupResult.success ? (
                          <div className="mt-1 text-sm text-green-700">
                            <p>File: {lastBackupResult.filename}</p>
                            {lastBackupResult.collections && (
                              <p className="mt-1">
                                Esportati: {Object.entries(lastBackupResult.collections).map(([coll, count]) => 
                                  `${coll}: ${count}`
                                ).join(', ')}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-red-700">{lastBackupResult.error}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Backup List */}
              <Card className="border-border/60">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Cloud className="h-5 w-5 text-primary" />
                        Backup Salvati
                      </CardTitle>
                      <CardDescription>
                        Lista dei backup presenti sul server FTP
                      </CardDescription>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={loadBackupList}
                      disabled={isLoadingBackups}
                    >
                      <RefreshCw className={`h-4 w-4 ${isLoadingBackups ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingBackups ? (
                    <div className="flex justify-center py-8">
                      <div className="spinner" />
                    </div>
                  ) : backupList.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      Nessun backup presente
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {backupList.map((backup, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            {backup.filename.includes('full') ? (
                              <HardDrive className="h-4 w-4 text-blue-600" />
                            ) : (
                              <Database className="h-4 w-4 text-green-600" />
                            )}
                            <div>
                              <p className="font-medium text-sm">{backup.filename}</p>
                              <p className="text-xs text-muted-foreground">{backup.date}</p>
                            </div>
                          </div>
                          <Badge variant="secondary">{backup.size_formatted}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>
    </div>
  );
};

export default SettingsPage;
